// src/pages/parts/UploadWidget.js
import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/api';
import VerificationModal from './VerificationModal';
import { useAuth } from '../contexts/AuthContext'; // <--- 1. Import Auth Hook
import './style/upload.css'; 
import './style/shared.css'; 

const UploadWidget = ({ onClose, onRefresh }) => {
  
  // --- Auth Context ---
  const { user } = useAuth(); // <--- 2. Get User Info

  // --- State: Data ---
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [categoryList, setCategoryList] = useState([]);
  
  const [startDate, setStartDate] = useState('');
  const [expireDate, setExpireDate] = useState('');
  const [alertTiming, setAlertTiming] = useState('');
  const [alertFrequency, setAlertFrequency] = useState('Weekly'); 

  // --- State: Organisation Search ---
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // --- State: UI & Status ---
  const [isDragging, setIsDragging] = useState(false);
  const [status, setStatus] = useState('idle'); 
  const [errorMessage, setErrorMessage] = useState('');
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  // --- Load Data ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1. Fetch Organisations
        const orgData = await apiService.getOrganisations();
        setOrganisations(orgData);

        // 2. Fetch Categories
        // THE FIX: The backend now handles all the logic (Admin vs Standard).
        // We just take what the API gives us.
        const catData = await apiService.getCategoryList();
        const safeCatList = Array.isArray(catData) ? catData : [];
        
        setCategoryList(safeCatList);

      } catch (error) {
        console.error("Failed to load data", error);
        setCategoryList([]);
      }
    };

    // We still check if user exists, but we don't need to check roles inside the logic anymore
    if (user) {
      fetchData();
    }
  }, [user]); 


  // --- Click Outside Dropdown ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- Drag & Drop Handlers ---
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) processFile(droppedFile);
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) processFile(selectedFile);
    e.target.value = ''; 
  };

  const processFile = (fileObj) => {
    setFile(fileObj);
    setName(fileObj.name); 
    setStatus('ready');
  };

  const removeFile = () => {
    setFile(null);
    setName('');
    setStatus('idle');
    setExpireDate(''); 
    setStartDate('');
    setAlertTiming('');
    setAlertFrequency('Weekly'); 
  };

  // --- Organisation Handlers ---
  const handleOrgSelect = (org) => {
    setSelectedOrgId(org.id);
    setSearchTerm(org.name);
    setShowDropdown(false);
  };

  const filteredOrgs = organisations.filter(org => 
    (org.name || '').toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  // --- Submit Handler ---
  const handleSubmit = async () => {
    if (!file || !selectedOrgId || !category) {
      setErrorMessage("Please select a file, an organisation, and a category.");
      return;
    }

    setStatus('uploading');
    setErrorMessage('');

    let daysToSend = 0;
    if (alertTiming === '1 Month Before') daysToSend = 30;
    else if (alertTiming === '2 Months Before') daysToSend = 60;
    else if (alertTiming === '3 Months Before') daysToSend = 90;
    else if (alertTiming === '4 Months Before') daysToSend = 120;
    else if (alertTiming === '5 Months Before') daysToSend = 150;

    try {
      const formData = new FormData();

      const selectedOrgObj = organisations.find(org => org.id === selectedOrgId);
      const orgNameToSend = selectedOrgObj ? selectedOrgObj.name : 'General';

      const selectedCatObj = categoryList.find(cat => cat.id == category);
      const catNameToSend = selectedCatObj ? selectedCatObj.name : 'Uncategorized';
      
      formData.append('organisationName', orgNameToSend);
      formData.append('categoryName', catNameToSend); 
      
      formData.append('organisationId', selectedOrgId);
      formData.append('category', category);
      
      const finalName = name.trim() !== '' ? name : file.name;
      formData.append('name', finalName);
      
      if (startDate) formData.append('startDate', startDate);
      if (expireDate) formData.append('expiryDate', expireDate);
      
      if (daysToSend > 0) {
        formData.append('alertTiming', daysToSend);
        formData.append('alertFrequency', alertFrequency);
      }

      formData.append('file', file);

      await apiService.uploadDocument(formData);
      
      setStatus('success');
      if (onRefresh) onRefresh();
      
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error) {
      console.error("Upload failed", error);
      setStatus('error');

      const isForbidden = 
        (error.response && error.response.status === 403) ||
        (error.message && (error.message.includes('Access Denied') || error.message.includes('Forbidden'))) ||
        (error.response && error.response.data && error.response.data.code === 'KEY_VERIFICATION_REQUIRED');

      if (isForbidden) {
        setShowVerificationModal(true);
      } else {
        setErrorMessage(error.response?.data?.message || "Upload failed. Please try again.");
      }
    }
  };

  // --- RENDER CONTENT ---
  const renderContent = () => {
    if (status === 'success') {
      return (
        <div className="status-container success">
          <div className="success-checkmark">
            <div className="check-icon"></div>
          </div>
          <h3>Upload Complete!</h3>
          <p>Your document has been successfully stored.</p>
        </div>
      );
    }

    if (status === 'uploading') {
      return (
        <div className="status-container processing">
          <div className="spinner"></div>
          <h3>Uploading...</h3>
          <p>Please wait while we process your file.</p>
        </div>
      );
    }

    return (
      <div className="upload-widget-container">
        <div style={{ padding: '1.5rem 1.5rem 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, color: '#334155' }}>Upload Document</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' }}>&times;</button>
        </div>

        {!file && (
          <div 
            className={`upload-area ${isDragging ? 'active' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById('fileInput').click()}
          >
            <span className="upload-icon">☁️</span>
            <h4 style={{ margin: '0.5rem 0', color: '#334155' }}>Click to upload or drag and drop</h4>
            <p className="upload-subtext">Please Upload Your File (Max 10MB)</p>
            <input 
              id="fileInput" 
              type="file" 
              className="hidden-input" 
              onChange={handleFileSelect} 
            />
          </div>
        )}

        {file && (
          <div className="upload-form">
            <div className="file-preview-card">
              <div className="file-icon">?</div>
              <div className="file-details">
                <span className="file-name-display">{file.name}</span>
                <span className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
              </div>
              <button className="remove-file-btn" onClick={removeFile}>Remove</button>
            </div>

            <div className="form-group">
              <label>Document Name <span className="required-mark">*</span></label>
              <input 
                type="text" 
                className="widget-input" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                autoComplete="off"
              />
            </div>

            <div className="form-group" ref={dropdownRef}>
              <label>Organisation <span className="required-mark">*</span></label>
              <div className="searchable-container">
                <input 
                  type="text" 
                  className="widget-input" 
                  placeholder="Search Organisation..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                    setSelectedOrgId('');
                  }}
                  onFocus={() => setShowDropdown(true)}
                />
                {showDropdown && (
                  <div className="suggestions-list">
                    {filteredOrgs.length > 0 ? (
                      filteredOrgs.map(org => (
                        <div 
                          key={org.id} 
                          className="suggestion-item"
                          onClick={() => handleOrgSelect(org)}
                        >
                          {org.name}
                        </div>
                      ))
                    ) : (
                      <div className="suggestion-item no-hover">No results found</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Category <span className="required-mark">*</span></label>
              <select 
                className="widget-input" 
                value={category} 
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">Select Category...</option>
                {categoryList.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              {/* Helper text if list is empty */}
              {categoryList.length === 0 && user?.role !== 'admin' && (
                <small style={{color: 'red'}}>You are not assigned to any categories.</small>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Start Date</label>
                <input 
                  type="date" 
                  className="widget-input" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Expiry Date</label>
                <input 
                  type="date" 
                  className="widget-input" 
                  value={expireDate} 
                  onChange={(e) => {
                    setExpireDate(e.target.value);
                    if (!e.target.value) setAlertTiming(''); 
                  }}
                />
              </div>
            </div>

            {expireDate && (
              <div 
                className="form-group" 
                style={{ 
                  animation: 'fadeIn 0.3s ease-in-out',
                  backgroundColor: '#f0f9ff', 
                  padding: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #bae6fd'
                }}
              >
                <label style={{ color: '#0369a1' }}>? Set Expiry Alert</label>
                <select 
                  className="widget-input" 
                  value={alertTiming} 
                  onChange={(e) => setAlertTiming(e.target.value)}
                  style={{ marginTop: '0.5rem', borderColor: '#bae6fd' }}
                >
                  <option value="">No Alert</option>
                  <option value="1 Month Before">1 Month Before</option>
                  <option value="2 Months Before">2 Months Before</option>
                  <option value="3 Months Before">3 Months Before</option>
                  <option value="4 Months Before">4 Months Before</option>
                  <option value="5 Months Before">5 Months Before</option>
                </select>

                {alertTiming && (
                  <div style={{ marginTop: '10px' }}>
                    <label style={{ fontSize: '0.85rem', color: '#0369a1', display:'block', marginBottom:'4px' }}>
                      Repeat Alert:
                    </label>
                    <select 
                      className="widget-input"
                      value={alertFrequency}
                      onChange={(e) => setAlertFrequency(e.target.value)}
                      style={{ borderColor: '#bae6fd' }}
                    >
                      <option value="Weekly">Weekly</option>
                      <option value="Monthly">Monthly</option>
                    </select>
                  </div>
                )}

                <small style={{ display: 'block', marginTop: '0.5rem', color: '#64748b' }}>
                  We will email you {alertFrequency.toLowerCase()} starting {alertTiming.toLowerCase()}.
                </small>
              </div>
            )}

          </div>
        )}

        <div className="widget-footer">
          <div className="error-msg">{errorMessage}</div>
          <div className="footer-buttons">
            <button className="action-btn secondary" onClick={onClose}>Cancel</button>
            <button 
              className="action-btn primary" 
              onClick={handleSubmit}
              disabled={!file || !selectedOrgId || !category}
            >
              Upload Document
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {showVerificationModal && (
        <VerificationModal 
          isOpen={showVerificationModal} 
          onClose={() => setShowVerificationModal(false)} 
        />
      )}

      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          {renderContent()}
        </div>
      </div>
    </>
  );
};

export default UploadWidget;
